import {
  CopyButton,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WORKING_BOX } from "@/components/tools/chemistry/formula-view";
import type { COMBINED_GAS_LAW_CALCULATOR_STRINGS } from "@/components/tools/combined-gas-law-calculator";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import type { GasTerm } from "@/lib/tool-kit/chem-physical/gas";
import { gasUnitSymbol } from "@/lib/tool-kit/chem-physical/gas";
import {
  entryKey,
  entrySymbol,
  type Factor,
  type GasLawSolution,
  lawEquation,
  otherState,
  rearrange,
  rearrangedText,
  workingUnitSymbol,
} from "@/lib/tool-kit/chem-physical/gas-laws";
import { formatPlain, formatSig } from "@/lib/tool-kit/chemistry/format";

type Strings = typeof COMBINED_GAS_LAW_CALCULATOR_STRINGS;

const TERMS: GasTerm[] = ["p", "v", "t", "n"];

export function GasLawAnswer({
  solution,
  texts,
  units,
  sig,
  strings,
}: {
  solution: GasLawSolution;
  texts: Record<string, string>;
  units: Record<GasTerm, string>;
  sig: number;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const { law, unknown, values } = solution;
  const symbol = entrySymbol(unknown.term, unknown.state);
  const answerSymbol = gasUnitSymbol(unknown.term, solution.answerUnit);
  const answerText = `${formatSig(fmt, solution.answer, sig)} ${answerSymbol}`;
  const workingSymbol = workingUnitSymbol(unknown.term, units[unknown.term]);
  const lawNames = {
    combined: strings.lawCombined,
    boyle: strings.lawBoyle,
    charles: strings.lawCharles,
    gayLussac: strings.lawGayLussac,
    avogadro: strings.lawAvogadro,
  } as const;

  const shown = (term: GasTerm, state: 1 | 2): string => {
    const key = entryKey(term, state);
    const unit = workingUnitSymbol(term, units[term]);
    const number =
      term === "t" ? formatPlain(fmt, values[key]) : texts[key].trim();
    return `${number} ${unit}`;
  };
  const join = (list: Factor[]): string => {
    const text = list.map((f) => shown(f.term, f.state)).join(" × ");
    return list.length > 1 ? `(${text})` : text;
  };
  const factors = rearrange(law, unknown);
  const top = factors.filter((f) => f.power === 1);
  const bottom = factors.filter((f) => f.power === -1);
  const numerator = top.length === 0 ? "1" : join(top);
  const expression =
    bottom.length === 0 ? numerator : `${numerator} ÷ ${join(bottom)}`;

  const conversions = TERMS.filter(
    (term) => term === "t" && units.t !== "K"
  ).flatMap((term) =>
    ([1, 2] as const)
      .filter((state) => !(unknown.term === term && unknown.state === state))
      .map((state) =>
        t(strings.convertLine, {
          symbol: entrySymbol(term, state),
          value: texts[entryKey(term, state)].trim(),
          unit: gasUnitSymbol(term, units[term]),
          converted: formatPlain(fmt, values[entryKey(term, state)]),
        })
      )
  );
  const needsConversion = unknown.term === "t" && units.t !== "K";
  const ratioLabel = `${symbol} ÷ ${entrySymbol(unknown.term, otherState(unknown.state))}`;
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={t(strings.answerLabel, { symbol })}
          value={answerText}
        />
        <StatCard
          label={ratioLabel}
          value={formatSig(fmt, solution.ratio, 3)}
        />
        <StatCard label={strings.lawUsed} value={lawNames[law]} />
      </StatGrid>
      <div className={WORKING_BOX}>
        <p className="font-medium font-sans text-foreground">
          {strings.working}
        </p>
        <p>{lawEquation(law)}</p>
        <p>{rearrangedText(law, unknown)}</p>
        {conversions.map((line) => (
          <p key={line}>{line}</p>
        ))}
        <p>
          {t(strings.substituted, {
            symbol,
            expression,
            result: formatSig(fmt, solution.working, sig + 1),
            unit: workingSymbol,
          })}
        </p>
        {needsConversion ? (
          <p>
            {t(strings.answerLine, {
              result: formatSig(fmt, solution.working, sig + 1),
              answer: answerText,
            })}
          </p>
        ) : null}
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={t(strings.copyText, { symbol, value: answerText })}
      />
    </>
  );
}
